import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import HubIcon from "@mui/icons-material/Hub";
import InputIcon from "@mui/icons-material/Input";
import InstallMobileIcon from "@mui/icons-material/InstallMobile";
import RocketLaunchOutlinedIcon from "@mui/icons-material/RocketLaunchOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import TroubleshootIcon from "@mui/icons-material/Troubleshoot";
import VideocamIcon from "@mui/icons-material/Videocam";
import { COLORS } from "./constants";
import { useInstallPrompt } from "./useInstallPrompt";
import { Wordmark } from "../Logo";

// ─────────────────────────────────────────────────────────────────────────────
// Sidebar
//
// Brand, navigation and who is signed in. Nothing else:
//  • the destination list and its controls live in DestinationsPanel (a second
//    copy here once let you stop an idle platform and get a bare 404);
//  • the INGEST / SRT status chips moved to the header's on-air panel, which
//    was already saying the same thing in a second place.
//
// `account` is the compact Navbar (name + log out), passed in by the page so
// the logout logic stays in one component.
// ─────────────────────────────────────────────────────────────────────────────

export const SIDEBAR_WIDTH = 264;

export const SECTION_IDS = {
  preview:      "section-preview",
  source:       "section-source",
  destinations: "section-destinations",
  diagnostics:  "section-diagnostics"
};

const NAV_ITEMS = [
  { id: SECTION_IDS.preview,      label: "Studio",        Icon: VideocamIcon },
  { id: SECTION_IDS.source,       label: "Source",        Icon: InputIcon },
  { id: SECTION_IDS.destinations, label: "Destinations",  Icon: HubIcon },
  { id: SECTION_IDS.diagnostics,  label: "Signal health", Icon: TroubleshootIcon }
];

const rowSx = {
  display: "flex", alignItems: "center", gap: 1.5,
  width: "100%", height: 44, px: 1.5,
  border: "none", borderRadius: "11px", cursor: "pointer",
  bgcolor: "transparent", color: COLORS.muted,
  font: "inherit", fontSize: 14, fontWeight: 500, textAlign: "left",
  textDecoration: "none",
  "& svg": { fontSize: 19 },
  "&:hover": { bgcolor: "rgba(239,227,204,0.05)", color: COLORS.text },
  "&:focus-visible": { outline: `3px solid ${COLORS.cyan}`, outlineOffset: 2 }
};

export function Sidebar({
  platforms, liveCount, isMobile, setSidebarOpen, onNavigate, account, onOpenSetup, storage
}) {
  const { canInstall, promptInstall } = useInstallPrompt();
  const [active, setActive] = React.useState(SECTION_IDS.preview);

  const jumpTo = (id) => {
    setActive(id);
    const el = typeof document !== "undefined" && document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    // On mobile the sidebar is a temporary Drawer over the content; leaving
    // it open would hide the section we just scrolled to.
    onNavigate?.();
  };

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", bgcolor: COLORS.band, px: 2, pt: 3, pb: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ pl: 1 }}>
        <Wordmark height={30} />
        {!isMobile && (
          <Tooltip title="Hide sidebar">
            <IconButton onClick={() => setSidebarOpen(false)} aria-label="Hide sidebar"
              sx={{ color: COLORS.muted, "&:hover": { color: COLORS.text } }}>
              <ChevronLeftIcon />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
      <Typography sx={{ mt: 1.25, pl: 1, color: COLORS.muted, fontSize: 12 }}>
        Studio · {platforms.length
          ? `${liveCount} of ${platforms.length} destination${platforms.length === 1 ? "" : "s"} live`
          : "no destinations yet"}
      </Typography>

      <Stack component="nav" aria-label="Dashboard sections" spacing={0.25} sx={{ mt: 4 }}>
        {NAV_ITEMS.map(({ id, label, Icon }) => {
          const on = active === id;
          return (
            <Box key={id} component="button" type="button" onClick={() => jumpTo(id)}
              aria-current={on ? "true" : undefined}
              sx={{
                ...rowSx,
                ...(on ? {
                  bgcolor: COLORS.panel, color: COLORS.text,
                  boxShadow: `inset 0 0 0 1px ${COLORS.border}`,
                  "& svg": { fontSize: 19, color: COLORS.cyan }
                } : {})
              }}>
              <Icon />
              {label}
            </Box>
          );
        })}
      </Stack>

      <Box sx={{ height: "1px", bgcolor: COLORS.border, my: 2, mx: 1 }} />

      <Stack spacing={0.25}>
        <Box component={RouterLink} to="/settings/security" onClick={onNavigate} sx={rowSx}>
          <PersonOutlineIcon />
          Profile
        </Box>
        {onOpenSetup && (
          <Box component="button" type="button"
            onClick={() => { onOpenSetup(); onNavigate?.(); }}
            sx={rowSx}>
            <RocketLaunchOutlinedIcon />
            Setup guide
          </Box>
        )}
        {/* Only once the browser has confirmed the app is installable, so it
            is never a button that does nothing. iOS never fires that event. */}
        {canInstall && (
          <Box component="button" type="button"
            onClick={() => { promptInstall(); onNavigate?.(); }}
            sx={{ ...rowSx, color: COLORS.cyan }}>
            <InstallMobileIcon />
            Install app
          </Box>
        )}
      </Stack>

      <Box sx={{ flexGrow: 1, minHeight: 24 }} />

      {/* MP4 storage, always in view: an upload that would go over the limit
          is refused, and this used to be visible only on the MP4 tab. */}
      {storage?.limitBytes > 0 && (() => {
        const used = storage.usedBytes || 0;
        const pct = Math.min(100, (used / storage.limitBytes) * 100);
        const tone = pct >= 90 ? COLORS.red : pct >= 70 ? COLORS.amber : COLORS.cyan;
        const gb = (b) => (b / 1e9).toFixed(b >= 1e10 ? 0 : 1);
        return (
          <Box sx={{ px: 1, pb: 2 }}>
            <Stack direction="row" justifyContent="space-between" sx={{ fontSize: 12, mb: 0.75 }}>
              <Box component="span" sx={{ color: COLORS.muted }}>Video storage</Box>
              <Box component="span" sx={{ color: COLORS.text, fontVariantNumeric: "tabular-nums" }}>
                {gb(used)} / {gb(storage.limitBytes)} GB
              </Box>
            </Stack>
            <Box role="meter" aria-label="Video storage used"
              aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(pct)}
              aria-valuetext={`${gb(used)} of ${gb(storage.limitBytes)} GB used`}
              sx={{ height: 8, borderRadius: 99, bgcolor: "rgba(43,189,175,0.16)", overflow: "hidden" }}>
              <Box sx={{ width: `${Math.max(pct, used > 0 ? 2 : 0)}%`, height: "100%", bgcolor: tone, borderRadius: 99 }} />
            </Box>
          </Box>
        );
      })()}

      {account && (
        <Box sx={{ pt: 2, px: 0.5, borderTop: `1px solid ${COLORS.border}` }}>
          {account}
        </Box>
      )}
    </Box>
  );
}
